/**
 * iter-170ah/am — Audit log viewer.
 *
 * Shows the user's own action history (movements, debts, profile,
 * API keys, spaces). Read-only paginated list with SERVER-SIDE filters
 * (action / target_type / date range / search) and proper pagination.
 *
 * Sources data from ``GET /api/audit-log/me``; the endpoint itself is
 * gated by the ``audit_log`` feature so this component only runs when
 * entitled (it lives inside a ``<LockedSection>`` already).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { History, ArrowDown, ArrowUp, Trash2, Plus, Pencil, Loader2, Filter, X, Search } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";

const PAGE_SIZE = 25;

const ACTION_META = {
  "movement.create":  { Icon: Plus,     tone: "bg-emerald-50 text-emerald-700" },
  "movement.update":  { Icon: Pencil,   tone: "bg-sky-50 text-sky-700" },
  "movement.delete":  { Icon: Trash2,   tone: "bg-rose-50 text-rose-700" },
  "debt.create":      { Icon: Plus,     tone: "bg-amber-50 text-amber-700" },
  "debt.update":      { Icon: Pencil,   tone: "bg-amber-50 text-amber-700" },
  "debt.delete":      { Icon: Trash2,   tone: "bg-rose-50 text-rose-700" },
  "api_key.create":   { Icon: Plus,     tone: "bg-violet-50 text-violet-700" },
  "api_key.revoke":   { Icon: Trash2,   tone: "bg-rose-50 text-rose-700" },
  "profile.update":   { Icon: Pencil,   tone: "bg-slate-100 text-slate-700" },
  "space.create":     { Icon: Plus,     tone: "bg-violet-50 text-violet-700" },
  "space.invite":     { Icon: Plus,     tone: "bg-sky-50 text-sky-700" },
  "space.invitation_accept": { Icon: Plus, tone: "bg-emerald-50 text-emerald-700" },
};

function fmtMetadata(md, lang) {
  void lang;
  const entries = Object.entries(md || {}).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return null;
  return (
    <span className="ml-2 inline-flex flex-wrap gap-1 items-center">
      {entries.slice(0, 4).map(([k, v]) => (
        <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
          {k}={String(v).slice(0, 40)}
        </span>
      ))}
    </span>
  );
}

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const locale = lang === "es" ? "es-CO" : lang === "pt" ? "pt-BR" : "en-US";
    return d.toLocaleString(locale, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function AuditLogViewer() {
  const { lang } = useLang();
  const [skip, setSkip] = useState(0);
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pull the distinct action list so the dropdown stays in sync with
  // whatever new action types backend code starts emitting.
  const { data: actionsData } = useQuery({
    queryKey: ["audit-log-actions"],
    queryFn: async () => (await api.get("/audit-log/me/actions")).data,
    staleTime: 60_000,
    retry: false,
  });
  const allActions = actionsData?.items || [];

  const params = { limit: PAGE_SIZE, skip };
  if (action) params.action = action;
  if (search) params.search = search;
  if (dateFrom) params.date_from = dateFrom;
  // Make `dateTo` inclusive by extending to the end of the day.
  if (dateTo) params.date_to = `${dateTo}T23:59:59.999Z`;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["audit-log-me", skip, action, search, dateFrom, dateTo],
    queryFn: async () => (await api.get("/audit-log/me", { params })).data,
    keepPreviousData: true,
    retry: false,
  });
  const items = data?.items || [];
  const total = data?.total || 0;

  const resetFilters = () => {
    setAction(""); setSearch(""); setSearchDraft("");
    setDateFrom(""); setDateTo(""); setSkip(0);
  };
  const onSearchSubmit = (e) => {
    e?.preventDefault?.();
    setSearch(searchDraft);
    setSkip(0);
  };

  const hasActiveFilters = Boolean(action || search || dateFrom || dateTo);

  return (
    <Card className="p-5 rounded-2xl" data-testid="audit-log-viewer">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-slate-700" />
        <h3 className="font-semibold text-slate-900">
          {tx(lang, "Audit log", "Historial de auditoría", "Histórico de auditoria")}
        </h3>
        {(isFetching || isLoading) && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        <span className="ml-auto text-[11px] text-slate-500 tabular-nums">
          {tx(lang, `${total} entries`, `${total} entradas`, `${total} entradas`)}
        </span>
        <Button
          size="sm"
          variant={showFilters || hasActiveFilters ? "default" : "outline"}
          onClick={() => setShowFilters((v) => !v)}
          data-testid="audit-log-toggle-filters"
          className="h-7 px-2 gap-1"
        >
          <Filter className="h-3 w-3" />
          {tx(lang, "Filters", "Filtros", "Filtros")}
          {hasActiveFilters && (
            <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-300" />
          )}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-3" data-testid="audit-log-filters-panel">
          {/* Search — not a real data-entry form, just a filter input + button. */}
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <Input
                type="text"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSearchSubmit(e); }}
                placeholder={tx(lang, "Search action or target id…", "Buscar acción o ID…", "Buscar ação ou ID…")}
                data-testid="audit-log-search-input"
                className="h-8 text-xs pl-8"
              />
            </div>
            <Button onClick={onSearchSubmit} size="sm" className="h-8 px-3" data-testid="audit-log-search-submit">
              {tx(lang, "Search", "Buscar", "Buscar")}
            </Button>
          </div>

          {/* Action dropdown */}
          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              {tx(lang, "Action", "Acción", "Ação")}
            </label>
            <Select
              value={action || "__all__"}
              onValueChange={(v) => { setAction(v === "__all__" ? "" : v); setSkip(0); }}
            >
              <SelectTrigger data-testid="audit-log-action-select" className="h-8 text-xs w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">{tx(lang, "All actions", "Todas las acciones", "Todas as ações")}</SelectItem>
                {allActions.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date range */}
          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              {tx(lang, "From", "Desde", "De")}
            </label>
            <Input
              type="date" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setSkip(0); }}
              data-testid="audit-log-date-from"
              className="h-8 text-xs w-36"
            />
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              {tx(lang, "To", "Hasta", "Até")}
            </label>
            <Input
              type="date" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setSkip(0); }}
              data-testid="audit-log-date-to"
              className="h-8 text-xs w-36"
            />
          </div>

          {hasActiveFilters && (
            <Button
              size="sm" variant="ghost"
              onClick={resetFilters}
              data-testid="audit-log-clear-filters"
              className="h-7 px-2 gap-1 text-rose-600 hover:bg-rose-50"
            >
              <X className="h-3 w-3" />
              {tx(lang, "Clear filters", "Limpiar filtros", "Limpar filtros")}
            </Button>
          )}
        </div>
      )}

      {items.length === 0 && !isLoading ? (
        <p className="text-xs text-slate-500 italic py-6 text-center" data-testid="audit-log-empty">
          {hasActiveFilters
            ? tx(lang, "No entries match these filters.", "Ninguna entrada coincide con estos filtros.", "Nenhuma entrada corresponde a esses filtros.")
            : tx(lang, "No activity yet.", "Sin actividad aún.", "Sem atividade ainda.")}
        </p>
      ) : (
        <ul className="space-y-1.5 divide-y divide-slate-100" data-testid="audit-log-list">
          {items.map((it) => {
            const meta = ACTION_META[it.action] || { Icon: History, tone: "bg-slate-100 text-slate-600" };
            const Icon = meta.Icon;
            return (
              <li key={it.id} className="flex items-start gap-3 py-2 text-sm">
                <span className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${meta.tone}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <code className="text-xs font-mono text-slate-800">{it.action}</code>
                    {it.target_id && (
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {it.target_id.slice(0, 8)}
                      </Badge>
                    )}
                    {fmtMetadata(it.metadata, lang)}
                  </div>
                  <p className="text-[11px] text-slate-500 tabular-nums">
                    {fmtDate(it.created_at, lang)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <span className="text-[11px] text-slate-500 tabular-nums">
          {items.length === 0
            ? "—"
            : tx(lang,
                `Showing ${skip + 1}–${skip + items.length} of ${total}`,
                `Mostrando ${skip + 1}–${skip + items.length} de ${total}`,
                `Mostrando ${skip + 1}–${skip + items.length} de ${total}`)}
        </span>
        <div className="flex gap-1">
          <Button
            size="sm" variant="outline"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            data-testid="audit-log-prev"
            className="h-7 px-2 gap-1"
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={skip + items.length >= total}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="audit-log-next"
            className="h-7 px-2 gap-1"
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
